import React, { useEffect, useRef, useState, useCallback } from "react";
import Slider from "react-slick"; // React Slick
import { collection, getDocs } from "firebase/firestore";
import { db } from '../../../firebase'; // Firestore 초기화 가져오기
import styleClasses from '../css/mobile/MusicArchive.module.scss'
import Loading from "../../Loading";
import { useParams } from "react-router-dom"; // useParams 가져오기



const MusicArchive = ({ isMobile }) => {
  const [activeTab, setActiveTab] = useState("ownAlbums");
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [prevButton, setPrevButton] = useState(styleClasses.hidden);
  const [nextButton, setNextButton] = useState(styleClasses.visible);

  // 각 탭의 데이터 유무 상태
  const [tabVisibility, setTabVisibility] = useState({
    ownAlbums: false,
    collaboAlbums: false,
    unreleasedAlbums: false,
  });

  const { id: musicianId } = useParams();

  // 🎯 Firestore에서 앨범 데이터만 먼저 가져오기 (트랙은 제외)
  const fetchAlbums = useCallback(async (albumType) => {
    try {
      const albumsRef = collection(db, "musicians", musicianId, albumType);
      const snapshot = await getDocs(albumsRef);
  
      if (snapshot.empty) {
        console.error(`No albums found in ${albumType}`);
        return [];
      }
  
      return snapshot.docs.map((albumDoc) => ({
        id: albumDoc.id,
        ...albumDoc.data(),
        year: new Date(albumDoc.data().year),
        tracks: [], // 🚀 트랙은 처음부터 불러오지 않음
      })).sort((a, b) => b.year - a.year); // 최신순 정렬
    } catch (error) {
      return [];
    }

  }, [musicianId]);

  // 🎯 Firestore에서 특정 앨범의 트랙 데이터 가져오기
  const fetchTracksForAlbum = useCallback(async (albumType, albumId) => {
    const tracksRef = collection(
      db,
      "musicians",
      musicianId,
      albumType,
      albumId,
      "tracks"
    );
    const tracksSnapshot = await getDocs(tracksRef);

    return tracksSnapshot.docs.map((trackDoc) => ({
      id: trackDoc.id,
      ...trackDoc.data(),
    }));
  }, [musicianId]);

  // 🚀 처음 로딩 시 최신 앨범 자동 선택 & 트랙 로드
  useEffect(() => {
    const checkTabs = async () => {
      setLoading(true);

      const ownAlbums = await fetchAlbums("ownAlbums");
      const collaboAlbums = await fetchAlbums("collaboAlbums");
      const unreleasedAlbums = await fetchAlbums("unreleasedAlbums");

      setTabVisibility({
        ownAlbums: ownAlbums.length > 0,
        collaboAlbums: collaboAlbums.length > 0,
        unreleasedAlbums: unreleasedAlbums.length > 0,
      });

      let initialTab = "";
      let initialAlbums = [];

      if (ownAlbums.length > 0) {
        initialTab = "ownAlbums";
        initialAlbums = ownAlbums;
      } else if (collaboAlbums.length > 0) {
        initialTab = "collaboAlbums";
        initialAlbums = collaboAlbums;
      } else if (unreleasedAlbums.length > 0) {
        initialTab = "unreleasedAlbums";
        initialAlbums = unreleasedAlbums;
      }

      setActiveTab(initialTab);
      setAlbums(initialAlbums);

      if (initialAlbums.length > 0) {
        const firstAlbum = initialAlbums[0];
        const tracks = await fetchTracksForAlbum(initialTab, firstAlbum.id);
        setSelectedAlbum({ ...firstAlbum, tracks });
      }

      setLoading(false);
    };

    checkTabs();
  }, [fetchAlbums, fetchTracksForAlbum]);

  // 탭 전환 및 데이터 가져오기
  useEffect(() => {
    setLoading(true);

    const fetchData = async () => {
      const albumData = await fetchAlbums(activeTab);
  
      if (albumData.length > 0) {
        const firstAlbum = albumData[0];
        const tracks = await fetchTracksForAlbum(activeTab, firstAlbum.id);
        setSelectedAlbum({ ...firstAlbum, tracks });
      }
  
      setAlbums(albumData);
      setLoading(false);
    };
  
    fetchData();
  }, [activeTab, fetchAlbums, fetchTracksForAlbum]); // ✅ 의존성 배열에 필요한 변수 추가

  const CustomPrevArrow = ({ className, style, onClick }) => (
    <button className={`${ styleClasses.arrow } ${ styleClasses.prev } ${ prevButton }`} onClick={onClick}>
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
        <path d="M13.293 6.293 7.586 12l5.707 5.707 1.414-1.414L10.414 12l4.293-4.293z"></path>
      </svg>
    </button>
  );
  const CustomNextArrow = ({ className, style, onClick }) => (
    <button className={`${ styleClasses.arrow } ${ styleClasses.next } ${ nextButton }`} onClick={ onClick }>
      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24">
        <path d="M10.707 17.707 16.414 12l-5.707-5.707-1.414 1.414L13.586 12l-4.293 4.293z"></path>
      </svg>
    </button>
  );

  // 슬라이더 설정
  const sliderSettings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    centerMode: true,
    centerPadding: 0,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    afterChange: async (index) => {
      if (index === 0) {
        setPrevButton(styleClasses.hidden);
      } else {
        setPrevButton(styleClasses.visible);
      }
      if (index === albums.length - 1) {
        setNextButton(styleClasses.hidden);
      } else {
        setNextButton(styleClasses.visible);
      }
    
      if (albums[index]) {
        let updatedAlbum = albums[index];
    
        if (!updatedAlbum.tracks || updatedAlbum.tracks.length === 0) {
          // 🚀 Firestore에서 트랙 데이터 가져오기
          const tracks = await fetchTracksForAlbum(activeTab, updatedAlbum.id);
          updatedAlbum = { ...updatedAlbum, tracks }; // ✅ 새로운 객체 생성 (트랙 추가)
        }
    
        setSelectedAlbum(updatedAlbum); // ✅ 새로운 객체로 설정하여 React 상태 감지
      }
    }    
  };

  const containerRef        = useRef();
  const ownAlbumsRef        = useRef();
  const collaboAlbumsRef    = useRef(); 
  const unreleasedAlbumsRef = useRef(); 
  const [isExpanded, setIsExpanded] = useState(false);

  const scrollToButton = (buttonRef) => {
    buttonRef.current.scrollIntoView({
      behavior: 'smooth', // 부드럽게 스크롤
      block: 'nearest',   // 수직 스크롤 방식 (사용하지 않음)
      inline: 'start',    // 버튼을 상위 요소의 맨 왼쪽에 위치
    });
  };

  return (
    
    <div className={ styleClasses.musicArchiveMobile }>
      <div>
        
      </div>
      {/* 탭 네비게이션 */}
      <div className={styleClasses.tabNavigation} ref={containerRef}>
        {tabVisibility.ownAlbums && (
          <button
            ref={ownAlbumsRef}
            className={`${styleClasses.tab} ${activeTab === "ownAlbums" ? styleClasses.active : ""}`}
            onClick={() => {
              setActiveTab("ownAlbums");
              setLoading(true);
              scrollToButton(ownAlbumsRef);
            }}
          >
            Musician Albums
          </button>
        )}
        {tabVisibility.collaboAlbums && (
          <button
            ref={collaboAlbumsRef}
            className={`${styleClasses.tab} ${activeTab === "collaboAlbums" ? styleClasses.active : ""}`}
            onClick={() => {
              setActiveTab("collaboAlbums");
              scrollToButton(collaboAlbumsRef);
            }}
          >
            Collaboration Albums
          </button>
        )}
        {tabVisibility.unreleasedAlbums && (
          <button
            ref={unreleasedAlbumsRef}
            className={`${styleClasses.tab} ${activeTab === "unreleasedAlbums" ? styleClasses.active : ""}`}
            onClick={() => {
              setActiveTab("unreleasedAlbums");
              scrollToButton(unreleasedAlbumsRef);
            }}
          >
            Unreleased
          </button>
        )}
      </div>
      {
        loading
        ?
        <Loading />
        :
        albums.length === 0
        ?
        (<p className={ styleClasses.noData }>No data available yet. :(</p>)
        :
        <>
          {/* 앨범 슬라이더 */}
          <Slider {...sliderSettings}>
            {albums.map((album) => (
              <div
                key={album.id}
                className={ styleClasses.albumCard }
                onClick={() => setSelectedAlbum(album)}
              >
                <div className={ styleClasses.albumWrap }>
                  <img src={album.cover} alt={album.title} className={ styleClasses.albumCover } />
                  <div className={ styleClasses.albumInfo }>
                    <h3 className={ styleClasses.albumTitle }>{album.title}</h3>
                    <p className={ styleClasses.albumYear }>{album.year.toLocaleDateString()}</p>
                    {album.artist && (<p className={ styleClasses.albumYear }>{album.artist}</p>)}
                  </div>
                </div>
              </div>
            ))}
          </Slider>
          {/* 앨범 설명 및 트랙리스트 */}
          {
            selectedAlbum &&
            (
            <div className={ styleClasses.albumDetails }>
              <h3 className={ styleClasses.albumDescriptionTitle }>{ selectedAlbum.title }</h3>
              {selectedAlbum.description && (
                <div className={styleClasses.albumDescription}>
                  {isExpanded ? (
                    selectedAlbum.description // ✅ '더보기' 버튼을 눌렀을 때 전체 내용 표시
                  ) : (
                    `${selectedAlbum.description.slice(0, 200)}...` // ✅ 200자까지만 표시
                  )}
                  {selectedAlbum.description.length > 200 && (
                    <button className={styleClasses.expandButton} onClick={() => setIsExpanded(!isExpanded)}>
                      {isExpanded ? "접기" : "더보기"}
                    </button>
                  )}
                </div>
              )}
              <ul className={ styleClasses.trackList }>
                {selectedAlbum.tracks.map((track, index) => (
                  <li key={track.id} className={ styleClasses.track }>
                    <span className={ styleClasses.trackNumber }>{index + 1} </span>
                    <a href={track.url} target="_blank" rel="noopener noreferrer" className={ styleClasses.trackTitle }>
                      {track.title.replace('[TITLE]', '').trim()}
                      {track.title.includes('[TITLE]') && (
                        <span className={ styleClasses.titleHighlight }>[TITLE]</span>
                      )}
                    </a>
                    <div className={ styleClasses.trackRoles }>
                      {track.roles
                        ?.split(',')
                        .map((role, idx) => (
                          <span key={idx} className={ styleClasses.roleTag }>
                            {role.trim()}
                          </span>
                        ))}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            )
          }
        </>
      }
    </div>
  );
};

export default MusicArchive;
